import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Popconfirm, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd'
import { autoAssignApi, type AutoAssignOperator, type AutoAssignSettings as Settings } from '../services/autoAssignApi'
import { roleLabel } from '../constants/roles'

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}



const EMPTY: Settings = {
    enabled: false,
    last_operator_id: null,
    last_operator_name: null,
    updated_at: null,
    operators: [],
}

export default function AutoAssignSettings() {
    const [settings, setSettings] = useState<Settings>(EMPTY)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await autoAssignApi.get()
            setSettings(data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    // Круг распределения: только включённые и работающие учётные записи, в том
    // же порядке, в каком их обходит сервер.
    const circle = useMemo(
        () => settings.operators.filter((item) => item.enabled && item.is_active).sort((a, b) => a.id - b.id),
        [settings.operators],
    )

    // Кому достанется следующий лид - тот же расчёт, что и на сервере.
    const nextOperator = useMemo(() => {
        if (!circle.length) return null
        const index = circle.findIndex((item) => item.id === settings.last_operator_id)
        return index >= 0 ? circle[(index + 1) % circle.length] : circle[0]
    }, [circle, settings.last_operator_id])

    const save = async (action: () => Promise<{ data: Settings }>) => {
        setSaving(true)
        try {
            const { data } = await action()
            setSettings(data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const columns = [
        {
            title: 'Сотрудник',
            key: 'name',
            render: (_: unknown, item: AutoAssignOperator) => (
                <Space direction="vertical" size={0}>
                    <Text strong={item.enabled}>{item.full_name || item.username}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{roleLabel(item.role)}</Text>
                </Space>
            ),
        },
        {
            title: 'Лидов сейчас',
            dataIndex: 'lead_count',
            width: 130,
            sorter: (a: AutoAssignOperator, b: AutoAssignOperator) => a.lead_count - b.lead_count,
            render: (value: number) => <Tag color={value ? 'blue' : 'default'}>{value}</Tag>,
        },
        {
            title: 'В распределении',
            key: 'enabled',
            width: 160,
            render: (_: unknown, item: AutoAssignOperator) => {
                if (!item.is_active) return <Text type="secondary">учётная запись отключена</Text>
                return (
                    <Popconfirm
                        title={item.enabled ? 'Убрать из распределения?' : 'Включить в распределение?'}
                        description={item.enabled
                            ? 'Новые лиды на этого сотрудника приходить перестанут. Уже назначенные останутся за ним.'
                            : 'Сотрудник встанет в круг и начнёт получать ответивших лидов наравне с остальными.'}
                        okText="Да"
                        cancelText="Отмена"
                        onConfirm={() => save(() => autoAssignApi.toggleOperator(item.id, !item.enabled))}
                    >
                        <Switch checked={item.enabled} disabled={saving} />
                    </Popconfirm>
                )
            },
        },
    ]

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <Space size={12} align="start">
                    <Popconfirm
                        title={settings.enabled ? 'Выключить автораспределение?' : 'Включить автораспределение?'}
                        description={settings.enabled
                            ? 'Ответившие лиды снова будут копиться в общем пуле «Ответили» и разбираться вручную.'
                            : 'Каждый лид, ответивший на первое сообщение, будет сразу уходить следующему сотруднику по кругу.'}
                        okText="Да"
                        cancelText="Отмена"
                        onConfirm={() => save(() => autoAssignApi.update({ enabled: !settings.enabled }))}
                    >
                        <Switch checked={settings.enabled} disabled={saving} />
                    </Popconfirm>
                    <div>
                        <div>Автоматически распределять ответивших лидов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Лид, которому ушло первое сообщение и который на него ответил, достаётся
                            следующему по кругу сотруднику из отмеченных ниже — по одному, поровну на каждого.
                            Диалог сразу уходит к нему в «Активные» и пропадает из общего пула «Ответили».
                            Пока переключатель выключен, всё работает как раньше: лидов разбирают вручную.
                        </Text>
                    </div>
                </Space>

                <Alert
                    type="info"
                    showIcon
                    message="Статус в CRM Wiseway меняется сразу при распределении"
                    description={
                        'Распределённой карточке тут же проставляется «Новый_назначен ЛПЕ\\ЛМ». '
                        + 'Это единственная запись в базу Wiseway во всём распределении, и она под тем же '
                        + 'переключателем «Передавать статусы», что и остальные наши смены статуса — '
                        + 'в разделе «Интеграция с CRM Wiseway». Пока он выключен, распределение работает '
                        + 'целиком внутри Оркестратора. Автором смены в их ленте становится тот сотрудник, '
                        + 'которому достался лид, поэтому у него должна быть заполнена почта Wiseway.'
                    }
                />

                {settings.enabled && nextOperator && (
                    <Alert
                        type="info"
                        showIcon
                        message={`Следующий ответивший лид уйдёт: ${nextOperator.full_name || nextOperator.username}`}
                        description={
                            `В круге ${circle.length} ${circle.length === 1 ? 'сотрудник' : 'сотрудников'}`
                            + (settings.last_operator_name ? `. Прошлый лид достался: ${settings.last_operator_name}` : '')
                        }
                    />
                )}

                {!circle.length && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Круг распределения пуст"
                        description="Отметьте хотя бы одну учётную запись — без этого автораспределение включить нельзя."
                    />
                )}

                <Table
                    rowKey="id"
                    size="small"
                    columns={columns}
                    dataSource={settings.operators}
                    pagination={false}
                />
            </Space>
        </Spin>
    )
}
